/**
 * G-72: filing a report — the one implementation of it.
 *
 * Both doors go through here: the florist tapping "Report an error", and the
 * error boundary filing silently after a crash. Two copies of "try to send,
 * otherwise queue" would drift, and the one that drifted would be the one
 * nobody tested — which is the silent one.
 */

import { floorRequest } from "../api/apiClient";
import {
  enqueueBugReport,
  flushBugReportQueue,
  type QueuedBugReport,
} from "./bugReportQueue";
import { consoleTail } from "./consoleBuffer";

/** Where a report ended up. The UI says different words for each. */
export type BugReportOutcome = "sent" | "queued";

function toForm(report: QueuedBugReport): FormData {
  const fd = new FormData();
  fd.append("message", report.message);
  fd.append("kind", report.kind);
  fd.append("occurredAt", report.occurredAt);
  if (report.screen) fd.append("screen", report.screen);
  if (report.consoleLog) fd.append("consoleLog", report.consoleLog);
  if (report.errorMessage) fd.append("errorMessage", report.errorMessage);
  if (report.errorStack) fd.append("errorStack", report.errorStack);
  if (report.userAgent) fd.append("userAgent", report.userAgent);
  if (report.viewportWidth !== undefined) {
    fd.append("viewportWidth", String(report.viewportWidth));
  }
  if (report.viewportHeight !== undefined) {
    fd.append("viewportHeight", String(report.viewportHeight));
  }
  return fd;
}

export async function sendBugReport(
  report: QueuedBugReport,
  screenshot?: File | null,
): Promise<void> {
  const form = toForm(report);
  if (screenshot) form.append("screenshot", screenshot, screenshot.name);
  await floorRequest("/shop-mode/bug-reports", { method: "POST", body: form });
}

/** Everything a report needs that the caller should not have to remember. */
export function buildBugReport(input: {
  message: string;
  kind: QueuedBugReport["kind"];
  screen?: string;
  errorMessage?: string;
  errorStack?: string;
}): QueuedBugReport {
  return {
    message: input.message,
    kind: input.kind,
    screen: input.screen,
    errorMessage: input.errorMessage,
    errorStack: input.errorStack,
    consoleLog: consoleTail(),
    occurredAt: new Date().toISOString(),
    userAgent:
      typeof navigator === "undefined" ? undefined : navigator.userAgent.slice(0, 500),
    viewportWidth: typeof window === "undefined" ? undefined : window.innerWidth,
    viewportHeight: typeof window === "undefined" ? undefined : window.innerHeight,
  };
}

/**
 * Send it, or hold it on the device.
 *
 * NEVER THROWS. A reporter that can fail is a reporter that adds a second
 * error on top of the one being reported — and the boundary calls this from
 * inside a screen that has already crashed.
 */
export async function submitBugReport(
  report: QueuedBugReport,
  screenshot?: File | null,
): Promise<BugReportOutcome> {
  try {
    await sendBugReport(report, screenshot);
    return "sent";
  } catch {
    // The line being down is often WHY they are reporting. The picture is NOT
    // queued with it — an image is megabytes against localStorage's ~5 MB
    // budget, shared with the printer registry and the dictation language, and
    // evicting those to keep a screenshot would be a poor trade.
    enqueueBugReport(report);
    return "queued";
  }
}

/** Try anything held from a previous shift. Returns how many are still waiting. */
export async function flushHeldBugReports(): Promise<number> {
  const { kept } = await flushBugReportQueue(sendBugReport);
  return kept;
}
